"use client";

import { useEffect, useMemo, useState } from "react";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import TabbedContent from "@/app/(components)/generics/TabbedContent/TabbedContent";
import NoAvatarCard from "@/app/(components)/specifics/tools/MessagesContent/components/NoAvatarCard/NoAvatarCard";
import Scripts from "@/app/(components)/specifics/tools/MessagesContent/components/Scripts/Scripts";
import Sequences from "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/Sequences";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import PageWrapper from "@/app/(components)/specifics/wrappers/PageWrapper/PageWrapper";
import { useInitialSelection } from "@/app/(components)/specifics/wrappers/SelectionProvider/SelectionProvider";
import { useFetchOfferAvatars } from "@/app/hooks/fetchs/client-avatar/useFetchOfferAvatars";
import { useFetchMessagesContentBootstrap } from "@/app/hooks/fetchs/messages-content/useFetchMessagesContentBootstrap";
import { useFetchProjectOffers } from "@/app/hooks/fetchs/offers/useFetchProjectOffers";
import { OfferStore } from "@/app/stores/OfferStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { PageBootstrap } from "@/app/types/common/WorkspaceSelection";
import { MessagesContentBootstrap } from "@/app/types/messages-content/MessagesContentBootstrap";
import { MessagesContentTab } from "@/app/types/messages-content/MessagesContentTab";
import { ScriptBootstrap } from "@/app/types/messages-content/ScriptBootstrap";
import { UCookie } from "@/app/utils/UCookie";
import { Limits } from "@/app/vars/Limits";
import { CookieKey, WORKSPACE_COOKIE_MAX_AGE } from "@/app/vars/Storage";
import { UrlParam } from "@/app/vars/Urls";
import styles from "./MessagesContent.module.css";

type Props = DefaultProps & {
  bootstrap: PageBootstrap<MessagesContentBootstrap> | null;
  scriptBootstrap: ScriptBootstrap | null;
};

export default function MessagesContent(props: Props) {
  const T = useT();
  const { params, bootstrap, scriptBootstrap } = props;

  const user = useAuthUser();
  const project = ProjectStore((state) => state.currentProject);
  const projectsFetched = ProjectStore((state) => state.projectsFetched);

  const initialSelection = useInitialSelection();

  const currentOfferId = OfferStore((state) => state.currentOfferId);
  const setStoreOffers = OfferStore((state) => state.setOffers);
  const selectedOfferId = currentOfferId ? String(currentOfferId) : "";

  const [selectedAvatarId, setSelectedAvatarId] = useState<string>(
    initialSelection.avatarId ? String(initialSelection.avatarId) : "",
  );

  const initialBootstrap = bootstrap?.projectId === project?.id ? bootstrap?.data : undefined;

  const { isSuccess: bootstrapReady } = useFetchMessagesContentBootstrap({
    projectId: project?.id,
    enabled: !!user && !!project?.id,
    initialData: initialBootstrap,
  });

  const { data: fetchedOffers } = useFetchProjectOffers({
    projectId: project?.id,
    enabled: !!user && !!project?.id && bootstrapReady,
  });

  const offers = useMemo(() => fetchedOffers ?? [], [fetchedOffers]);
  const offersLoaded = fetchedOffers !== undefined;

  const currentOfferHasAvatar = offers.some(
    (offer) => offer.id === currentOfferId && (offer._count?.clientAvatars ?? 0) > 0,
  );

  const { data: fetchedAvatars } = useFetchOfferAvatars({
    offerId: selectedOfferId ? parseInt(selectedOfferId, 10) : undefined,
    enabled: !!user && !!selectedOfferId && bootstrapReady,
  });

  const avatars = useMemo(() => fetchedAvatars ?? [], [fetchedAvatars]);
  const avatarsLoaded = fetchedAvatars !== undefined;

  const avatarIdNum = selectedAvatarId ? parseInt(selectedAvatarId, 10) : undefined;

  useEffect(() => {
    if (offersLoaded && project?.id) {
      setStoreOffers(project.id, offers);
    }
  }, [offersLoaded, offers, project?.id, setStoreOffers]);

  useEffect(() => {
    if (!avatarsLoaded) {
      return;
    }
    if (avatars.length === 0) {
      setSelectedAvatarId("");
      return;
    }
    setSelectedAvatarId(avatars[0] ? String(avatars[0].id) : "");
  }, [avatarsLoaded, avatars]);

  useEffect(() => {
    if (selectedOfferId) {
      UCookie.write(CookieKey.SELECTED_OFFER, selectedOfferId, WORKSPACE_COOKIE_MAX_AGE);
    }
  }, [selectedOfferId]);

  useEffect(() => {
    if (selectedAvatarId) {
      UCookie.write(CookieKey.SELECTED_AVATAR, selectedAvatarId, WORKSPACE_COOKIE_MAX_AGE);
    }
  }, [selectedAvatarId]);

  const renderTabs = (isConnected: boolean) => (
    <TabbedContent
      urlParamName={UrlParam.TAB}
      hideContentPadding={true}
      items={[
        {
          id: MessagesContentTab.SEQUENCES,
          title: T(TKey.MESSAGES_CONTENT_TAB_SEQUENCES) as string,
          content: (
            <div className={styles.tabContent}>
              <Sequences params={params} avatarId={avatarIdNum} isConnected={isConnected} />
            </div>
          ),
        },
        {
          id: MessagesContentTab.SCRIPTS,
          title: T(TKey.MESSAGES_CONTENT_TAB_SCRIPTS) as string,
          content: (
            <div className={styles.tabContent}>
              <Scripts
                params={params}
                avatarId={avatarIdNum}
                isConnected={isConnected}
                bootstrap={scriptBootstrap}
              />
            </div>
          ),
        },
        {
          id: MessagesContentTab.CONTENTS,
          title: `${T(TKey.MESSAGES_CONTENT_TAB_CONTENTS)} (${T(TKey.COMMON_SOON)})`,
          content: <></>,
          disabled: true,
        },
      ]}
    />
  );

  const renderBody = () => {
    if (!user) {
      return renderTabs(false);
    }

    if (!projectsFetched || !bootstrapReady) {
      return (
        <div className={styles.loaderContainer}>
          <PointsLoader color="var(--color-green-3)" delay={Limits.PROSPECT_SEARCH_LOADER_DELAY} />
        </div>
      );
    }

    if (!currentOfferHasAvatar) {
      return <NoAvatarCard params={params} />;
    }

    return renderTabs(true);
  };

  return (
    <PageWrapper
      title={
        <span className={styles.titleWithBeta}>
          {T(TKey.MESSAGES_CONTENT_TITLE)}
          <span className={styles.betaBadge}>{T(TKey.COMMON_BETA)}</span>
        </span>
      }
      subtitle={T(TKey.MESSAGES_CONTENT_SUBTITLE)}
    >
      <div className={styles.mainBody}>{renderBody()}</div>
    </PageWrapper>
  );
}
